/**
 * useBpmDialog — Track 3 Phase 5
 *
 * Owns the Block Properties Manager (BPM) dialog feature (Phase N.5)
 * extracted from `App.tsx` as part of the Track 3 App.tsx
 * decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Owns `bpmDialogOpen` and `bpmInitialBlock` local state.
 *   - Exposes imperative `open()` / `close()` for the command-line
 *     router (BLOCKPROPERTIESMANAGER / BPM / ДСБЛОК) and
 *     `resetShellState()`. `open(blockName?)` lets future entry points
 *     (context menu, ribbon button) preselect a specific block.
 *   - Owns the 9 BPM commit handlers — each dispatches the matching
 *     `geometry/...-block-...` command. Errors that surface from the
 *     mutation layer (collision, missing block, invalid metadata) are
 *     funneled through the command feed.
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     props: {
 *       drawingDocument,
 *       initialBlockName,
 *       onRenameParameter, onUpdateParameterDescription,
 *       onTogglePaletteVisibility, onReorderParameters,
 *       onDeleteParameter,
 *       onRenameAction, onUpdateActionDescription,
 *       onReorderActions, onDeleteAction,
 *       onClose,
 *     },
 *     open, close,
 *   }
 *
 * The orchestrator mounts `<BlockPropertiesManagerDialog>` when
 * `isOpen` and spreads `props`. AutoCAD parity: the manager works
 * regardless of active space (no BEDIT requirement) — see
 * `apps/web/src/app/components/BlockPropertiesManagerDialog.tsx`.
 */

import { useCallback, useState } from "react";

import { useDialogOpenState } from "./useDialogOpenState";
import { useEditorStore } from "../state/editorStore";
import type {
  BpmDeleteActionCommit,
  BpmDeleteParameterCommit,
  BpmRenameActionCommit,
  BpmRenameParameterCommit,
  BpmReorderActionsCommit,
  BpmReorderParametersCommit,
  BpmTogglePaletteCommit,
  BpmUpdateActionDescriptionCommit,
  BpmUpdateParameterDescriptionCommit,
} from "../components/BlockPropertiesManagerDialog";

export interface UseBpmDialogParams {
  /** Receives localized error feed entries surfaced from mutation failures. */
  appendCommandFeed: (text: string) => void;
}

export interface UseBpmDialogResult {
  /** Whether to mount `<BlockPropertiesManagerDialog>`. */
  isOpen: boolean;
  /** Props bundle for `<BlockPropertiesManagerDialog>`. */
  props: {
    drawingDocument: ReturnType<
      typeof useEditorStore.getState
    >["geometryDocument"];
    initialBlockName: string | undefined;
    onRenameParameter: (commit: BpmRenameParameterCommit) => void;
    onUpdateParameterDescription: (
      commit: BpmUpdateParameterDescriptionCommit,
    ) => void;
    onTogglePaletteVisibility: (commit: BpmTogglePaletteCommit) => void;
    onReorderParameters: (commit: BpmReorderParametersCommit) => void;
    onDeleteParameter: (commit: BpmDeleteParameterCommit) => void;
    onRenameAction: (commit: BpmRenameActionCommit) => void;
    onUpdateActionDescription: (
      commit: BpmUpdateActionDescriptionCommit,
    ) => void;
    onReorderActions: (commit: BpmReorderActionsCommit) => void;
    onDeleteAction: (commit: BpmDeleteActionCommit) => void;
    onClose: () => void;
  };
  /** Imperative open. Optional `blockName` preselects a block in the dialog. */
  open: (blockName?: string) => void;
  /** Imperative close — used by `resetShellState()` and `onClose`. */
  close: () => void;
}

export function useBpmDialog({
  appendCommandFeed,
}: UseBpmDialogParams): UseBpmDialogResult {
  const dispatch = useEditorStore((store) => store.dispatch);
  const geometryDocument = useEditorStore((store) => store.geometryDocument);

  const [initialBlockName, setInitialBlockName] = useState<string | undefined>(
    undefined,
  );
  const { isOpen, open: openDialog, close } = useDialogOpenState({
    onClose: () => setInitialBlockName(undefined),
  });

  const open = useCallback(
    (blockName?: string) => {
      setInitialBlockName(blockName);
      openDialog();
    },
    [openDialog],
  );

  // ── BPM handlers ────────────────────────────────────────────
  // Each handler dispatches the matching `geometry/...-block-...` command
  // and lets the (BEDIT-agnostic) reducer apply the mutation. Errors that
  // surface from the mutation layer (collision, missing block, invalid
  // metadata) are surfaced via the command feed.
  const onRenameParameter = useCallback(
    (commit: BpmRenameParameterCommit) => {
      try {
        dispatch({
          type: "geometry/rename-block-parameter",
          source: "toolbar",
          blockName: commit.blockName,
          parameterId: commit.parameterId,
          newName: commit.newName,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        appendCommandFeed(message);
      }
    },
    [dispatch, appendCommandFeed],
  );

  const onUpdateParameterDescription = useCallback(
    (commit: BpmUpdateParameterDescriptionCommit) => {
      dispatch({
        type: "geometry/update-block-parameter-description",
        source: "toolbar",
        blockName: commit.blockName,
        parameterId: commit.parameterId,
        description: commit.description,
      });
    },
    [dispatch],
  );

  const onTogglePaletteVisibility = useCallback(
    (commit: BpmTogglePaletteCommit) => {
      dispatch({
        type: "geometry/toggle-parameter-show-in-palette",
        source: "toolbar",
        blockName: commit.blockName,
        parameterId: commit.parameterId,
      });
    },
    [dispatch],
  );

  const onReorderParameters = useCallback(
    (commit: BpmReorderParametersCommit) => {
      dispatch({
        type: "geometry/reorder-block-parameters",
        source: "toolbar",
        blockName: commit.blockName,
        newOrder: commit.newOrder,
      });
    },
    [dispatch],
  );

  const onDeleteParameter = useCallback(
    (commit: BpmDeleteParameterCommit) => {
      // Reuse the existing remove-dynamic-parameter command — the BEDIT
      // gate inside that handler is intentionally skipped via a transient
      // BEDIT switch is NOT viable (would change spaces). Instead the BPM
      // dialog calls this only for blocks the user has authoring rights
      // on; if the gate rejects the dispatch, the mutation is a no-op and
      // the reducer falls through with no state change. Future work can
      // route delete through a non-gated path if BPM needs to delete
      // outside BEDIT for AutoCAD parity.
      dispatch({
        type: "geometry/remove-dynamic-parameter",
        source: "toolbar",
        blockName: commit.blockName,
        parameterId: commit.parameterId,
      });
    },
    [dispatch],
  );

  const onRenameAction = useCallback(
    (commit: BpmRenameActionCommit) => {
      try {
        dispatch({
          type: "geometry/rename-block-action",
          source: "toolbar",
          blockName: commit.blockName,
          actionId: commit.actionId,
          newName: commit.newName,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        appendCommandFeed(message);
      }
    },
    [dispatch, appendCommandFeed],
  );

  const onUpdateActionDescription = useCallback(
    (commit: BpmUpdateActionDescriptionCommit) => {
      dispatch({
        type: "geometry/update-block-action-description",
        source: "toolbar",
        blockName: commit.blockName,
        actionId: commit.actionId,
        description: commit.description,
      });
    },
    [dispatch],
  );

  const onReorderActions = useCallback(
    (commit: BpmReorderActionsCommit) => {
      dispatch({
        type: "geometry/reorder-block-actions",
        source: "toolbar",
        blockName: commit.blockName,
        newOrder: commit.newOrder,
      });
    },
    [dispatch],
  );

  const onDeleteAction = useCallback(
    (commit: BpmDeleteActionCommit) => {
      dispatch({
        type: "geometry/remove-dynamic-action",
        source: "toolbar",
        blockName: commit.blockName,
        actionId: commit.actionId,
      });
    },
    [dispatch],
  );

  return {
    isOpen,
    props: {
      drawingDocument: geometryDocument,
      initialBlockName,
      onRenameParameter,
      onUpdateParameterDescription,
      onTogglePaletteVisibility,
      onReorderParameters,
      onDeleteParameter,
      onRenameAction,
      onUpdateActionDescription,
      onReorderActions,
      onDeleteAction,
      onClose: close,
    },
    open,
    close,
  };
}
